import { Button, Loader, Modal } from "@mantine/core";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import clsx from "clsx";
import { Suspense, lazy } from "react";

type EmojiMartSelection = {
	native: string;
};

/** emoji-mart default width; kept fixed so the modal doesn't shrink on close */
const EMOJI_PICKER_WIDTH = 352;

const LazyEmojiMartPicker = lazy(async () => {
	const [{ default: Picker }, dataModule] = await Promise.all([
		import("@emoji-mart/react"),
		import("@emoji-mart/data"),
	]);

	const data =
		"default" in dataModule && dataModule.default
			? dataModule.default
			: dataModule;

	function EmojiMartPicker({
		onEmojiSelect,
	}: {
		onEmojiSelect: (emoji: EmojiMartSelection) => void;
	}) {
		return (
			<Picker
				data={data}
				onEmojiSelect={onEmojiSelect}
				theme="auto"
				set="native"
				width={EMOJI_PICKER_WIDTH}
				previewPosition="none"
				skinTonePosition="none"
				navPosition="top"
				searchPosition="sticky"
				icons="outline"
				autoFocus
				perLine={9}
				emojiButtonSize={36}
				emojiSize={22}
				maxFrequentRows={2}
			/>
		);
	}

	return { default: EmojiMartPicker };
});

interface EmojiPickerModalProps {
	opened: boolean;
	onClose: () => void;
	currentEmoji: string | null;
	onSelect: (emoji: string) => void;
	onRemove?: () => void;
	title?: string;
}

export function EmojiPickerModal({
	opened,
	onClose,
	currentEmoji,
	onSelect,
	onRemove,
	title = "Choose icon",
}: EmojiPickerModalProps) {
	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={<span className="font-semibold text-sm">{title}</span>}
			size={EMOJI_PICKER_WIDTH}
			centered
			zIndex={10000}
			padding={0}
			// emoji-mart scrolls inside shadow DOM; RemoveScroll cannot see it
			lockScroll={false}
			classNames={{
				content: clsx(
					"bg-surface-default overflow-hidden",
					"w-[352px] max-w-[calc(100vw-2rem)]",
				),
				header: "bg-surface-default px-4 py-3 min-h-0",
				body: "bg-surface-default p-0 overflow-hidden",
			}}
		>
			<div className="flex flex-col">
				<div className="emoji-picker-host">
					<Suspense
						fallback={
							<div className="flex h-full w-full items-center justify-center">
								<Loader color="var(--ui-color-brand-primary)" type="dots" />
							</div>
						}
					>
						<LazyEmojiMartPicker
							onEmojiSelect={(emoji: EmojiMartSelection) => {
								if (!emoji.native) return;
								onSelect(emoji.native);
								onClose();
							}}
						/>
					</Suspense>
				</div>

				{currentEmoji && onRemove ? (
					<div className="border-t border-border-subtle px-3 py-2">
						<Button
							variant="subtle"
							color="red"
							size="xs"
							leftSection={<IconTrash className="w-3.5 h-3.5" />}
							onClick={() => {
								onRemove();
								onClose();
							}}
							fullWidth
						>
							Remove icon
						</Button>
					</div>
				) : null}
			</div>
		</Modal>
	);
}
